import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, FormControlLabel, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { type SandboxCourse } from '../../utils/sandboxCourseApi';
import { fetchSandboxCourse, updateSandboxCourse } from '../../redux/sandbox/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { SandboxPrivateProjectPicker } from './SandboxPrivateProjectPicker';
import type { IProject } from '../../types/global/helper';

type ClassRow = {
    key: string;
    _id?: string;
    title: string;
    description: string;
    projectId: string;
    projectTitle?: string;
    is_free_preview: boolean;
    has_assignment: boolean;
};

type Props = {
    open: boolean;
    courseId: string | null;
    onClose: () => void;
    onSaved?: () => void;
};

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: '#323232',
        color: OBSIDIAN.onSurface,
        borderRadius: 1.5,
        fontFamily: OBSIDIAN_BODY_FONT,
        '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
        '&:hover fieldset': { borderColor: OBSIDIAN.outline },
        '&.Mui-focused fieldset': { borderColor: OBSIDIAN.primary },
    },
    '& .MuiInputBase-input': {
        color: OBSIDIAN.onSurface,
        '&::placeholder': { color: OBSIDIAN.onSurfaceVariant, opacity: 1 },
    },
};

function flattenClasses(course: SandboxCourse): ClassRow[] {
    const rows: ClassRow[] = [];
    (course.modules || [])
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .forEach(mod => {
            (mod.lessons || [])
                .slice()
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .forEach(lesson => {
                    rows.push({
                        key: lesson._id || `${mod._id}-${lesson.title}`,
                        _id: lesson._id,
                        title: lesson.title,
                        description: lesson.description || '',
                        projectId: (lesson.project as string) || '',
                        projectTitle: undefined,
                        is_free_preview: Boolean((lesson as any).is_free_preview),
                        has_assignment: Boolean(lesson.has_assignment),
                    });
                });
        });
    return rows;
}

function toModules(classes: ClassRow[]) {
    return [
        {
            title: 'Curriculum',
            order: 0,
            lessons: classes.map((c, i) => ({
                ...(c._id ? { _id: c._id } : {}),
                title: c.title.trim() || `Class ${i + 1}`,
                description: (c.description || '').trim(),
                order: i,
                project: c.projectId.trim() || null,
                is_free_preview: c.is_free_preview,
                has_assignment: c.has_assignment,
            })),
        },
    ];
}

export function SandboxManageClassesModal({ open, courseId, onClose, onSaved }: Props) {
    const dispatchPromise = useDispatchPromise();
    const [course, setCourse] = useState<SandboxCourse | null>(null);
    const [classes, setClasses] = useState<ClassRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [pickerIndex, setPickerIndex] = useState<number | null>(null);

    useEffect(() => {
        if (!open || !courseId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res: any = await dispatchPromise(fetchSandboxCourse(courseId));
                const payload = res?.data || res;
                const c = payload?.course || payload;
                if (cancelled) return;
                setCourse(c);
                setClasses(flattenClasses(c));
            } catch (e: any) {
                showToastMessage(e?.message || 'Could not load course', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, courseId, dispatchPromise]);

    const move = (index: number, dir: -1 | 1) => {
        const next = index + dir;
        if (next < 0 || next >= classes.length) return;
        setClasses(prev => {
            const copy = [...prev];
            const tmp = copy[index];
            copy[index] = copy[next];
            copy[next] = tmp;
            return copy;
        });
    };

    const addClass = () => {
        setClasses(prev => [
            ...prev,
            {
                key: `new-${Date.now()}`,
                title: `Class ${prev.length + 1}`,
                description: '',
                projectId: '',
                projectTitle: undefined,
                is_free_preview: prev.length === 0,
                has_assignment: false,
            },
        ]);
    };

    const onPickProject = (project: IProject) => {
        if (pickerIndex == null) return;
        setClasses(prev =>
            prev.map((c, i) =>
                i === pickerIndex
                    ? { ...c, projectId: String(project._id), projectTitle: project.title || 'Private project' }
                    : c,
            ),
        );
        setPickerIndex(null);
    };

    const save = async () => {
        if (!courseId) return;
        if (classes.some(c => !c.title.trim())) {
            showToastMessage('Every class needs a title', 'error');
            return;
        }
        setSaving(true);
        try {
            await dispatchPromise(updateSandboxCourse({ courseId, body: { modules: toModules(classes) as any } }));
            showToastMessage('Classes updated', 'success');
            onSaved?.();
            onClose();
        } catch (e: any) {
            showToastMessage(e?.message || 'Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                PaperProps={{
                    sx: {
                        bgcolor: OBSIDIAN.surfaceContainerLow,
                        backgroundImage: 'none',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.85)',
                        maxHeight: 'calc(100dvh - 32px)',
                    },
                }}
                slotProps={{
                    backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' } },
                }}>
                <Box
                    sx={{
                        px: 3,
                        pt: 3,
                        pb: 2,
                        bgcolor: OBSIDIAN.surfaceContainer,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: OBSIDIAN.primary,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}>
                            Curriculum Builder · Step 2 of 3
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                fontSize: 20,
                                color: OBSIDIAN.onSurface,
                                mt: 0.5,
                            }}>
                            Classes in {course?.title || 'course'}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 12,
                                color: OBSIDIAN.onSurfaceVariant,
                                mt: 0.5,
                            }}>
                            Attach a private project for each class. Students pay once for the course.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: OBSIDIAN.onSurfaceVariant, height: 36 }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={previewMode}
                                onChange={e => setPreviewMode(e.target.checked)}
                                sx={{ color: OBSIDIAN.outline, '&.Mui-checked': { color: OBSIDIAN.secondary } }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                                Preview as student
                            </Typography>
                        }
                    />
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addClass}
                        sx={{ color: OBSIDIAN.primary, textTransform: 'none', fontWeight: 700 }}>
                        Add class
                    </Button>
                </Box>

                <Box
                    sx={{
                        px: 3,
                        pb: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        maxHeight: '55vh',
                        overflowY: 'auto',
                    }}>
                    {loading ? (
                        <Typography sx={{ color: OBSIDIAN.onSurfaceVariant }}>Loading…</Typography>
                    ) : classes.length === 0 ? (
                        <Box sx={{ p: 3, borderRadius: 2, bgcolor: OBSIDIAN.surfaceContainer, textAlign: 'center' }}>
                            <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, mb: 1.5 }}>No classes yet</Typography>
                            <Button onClick={addClass} sx={{ color: OBSIDIAN.primary, textTransform: 'none' }}>
                                Add your first class
                            </Button>
                        </Box>
                    ) : (
                        classes.map((row, index) => {
                            const locked = previewMode && !row.is_free_preview;
                            return (
                                <Box
                                    key={row.key}
                                    sx={{
                                        p: 1.75,
                                        borderRadius: 2,
                                        bgcolor: OBSIDIAN.surfaceContainer,
                                        opacity: locked ? 0.55 : 1,
                                        border: row.is_free_preview
                                            ? `1px solid ${OBSIDIAN.secondary}44`
                                            : `1px solid ${OBSIDIAN.outline}22`,
                                    }}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => move(index, -1)}
                                                disabled={index === 0}
                                                sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => move(index, 1)}
                                                disabled={index === classes.length - 1}
                                                sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    color: OBSIDIAN.onSurfaceVariant,
                                                    fontWeight: 700,
                                                }}>
                                                CLASS {index + 1}
                                                {row.is_free_preview ? ' · FREE PREVIEW' : ' · PAID (course unlock)'}
                                                {locked ? ' · LOCKED IN PREVIEW' : ''}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                value={row.title}
                                                onChange={e =>
                                                    setClasses(prev =>
                                                        prev.map((c, i) =>
                                                            i === index ? { ...c, title: e.target.value } : c,
                                                        ),
                                                    )
                                                }
                                                placeholder="Class title"
                                                sx={fieldSx}
                                            />
                                            <TextField
                                                size="small"
                                                multiline
                                                minRows={2}
                                                value={row.description}
                                                onChange={e =>
                                                    setClasses(prev =>
                                                        prev.map((c, i) =>
                                                            i === index ? { ...c, description: e.target.value } : c,
                                                        ),
                                                    )
                                                }
                                                placeholder="Class description (shown in Overview)"
                                                sx={fieldSx}
                                            />
                                            <Button
                                                fullWidth
                                                startIcon={
                                                    row.projectId ? <LockOutlinedIcon /> : <VideoLibraryOutlinedIcon />
                                                }
                                                onClick={() => setPickerIndex(index)}
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    textTransform: 'none',
                                                    bgcolor: '#323232',
                                                    color: row.projectId
                                                        ? OBSIDIAN.onSurface
                                                        : OBSIDIAN.onSurfaceVariant,
                                                    border: `1px solid ${OBSIDIAN.outline}55`,
                                                    borderRadius: 1.5,
                                                    px: 1.5,
                                                    py: 1,
                                                    fontFamily: OBSIDIAN_BODY_FONT,
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    '&:hover': {
                                                        bgcolor: '#3a3a3a',
                                                        borderColor: OBSIDIAN.outline,
                                                    },
                                                }}>
                                                {row.projectTitle ||
                                                    (row.projectId
                                                        ? `Private project · ${row.projectId.slice(-8)}`
                                                        : 'Pick private project (HLS)')}
                                            </Button>
                                            {row.projectId ? (
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        setClasses(prev =>
                                                            prev.map((c, i) =>
                                                                i === index
                                                                    ? { ...c, projectId: '', projectTitle: undefined }
                                                                    : c,
                                                            ),
                                                        )
                                                    }
                                                    sx={{
                                                        alignSelf: 'flex-start',
                                                        color: OBSIDIAN.onSurfaceVariant,
                                                        textTransform: 'none',
                                                        fontSize: 11,
                                                        minWidth: 0,
                                                        p: 0,
                                                    }}>
                                                    Clear project
                                                </Button>
                                            ) : null}
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={row.is_free_preview}
                                                            onChange={e =>
                                                                setClasses(prev =>
                                                                    prev.map((c, i) =>
                                                                        i === index
                                                                            ? {
                                                                                  ...c,
                                                                                  is_free_preview: e.target.checked,
                                                                              }
                                                                            : c,
                                                                    ),
                                                                )
                                                            }
                                                            sx={{
                                                                color: OBSIDIAN.outline,
                                                                '&.Mui-checked': { color: OBSIDIAN.secondary },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurface }}>
                                                            Free preview
                                                        </Typography>
                                                    }
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={row.has_assignment}
                                                            onChange={e =>
                                                                setClasses(prev =>
                                                                    prev.map((c, i) =>
                                                                        i === index
                                                                            ? {
                                                                                  ...c,
                                                                                  has_assignment: e.target.checked,
                                                                              }
                                                                            : c,
                                                                    ),
                                                                )
                                                            }
                                                            sx={{
                                                                color: OBSIDIAN.outline,
                                                                '&.Mui-checked': { color: OBSIDIAN.primary },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurface }}>
                                                            Assignment
                                                        </Typography>
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => setClasses(prev => prev.filter((_, i) => i !== index))}
                                            sx={{ color: '#ff7351' }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </Box>

                <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 1.5 }}>
                    <Button
                        onClick={onClose}
                        sx={{ color: OBSIDIAN.onSurfaceVariant, textTransform: 'none', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        disabled={saving || loading}
                        onClick={save}
                        sx={{
                            flex: 1,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.primary,
                            color: OBSIDIAN.onPrimaryFixed,
                            textTransform: 'none',
                            fontWeight: 800,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            '&:hover': { bgcolor: OBSIDIAN.primaryDim },
                        }}>
                        Save classes
                    </Button>
                </Box>
            </Dialog>

            <SandboxPrivateProjectPicker
                open={pickerIndex != null}
                onClose={() => setPickerIndex(null)}
                onSelect={onPickProject}
            />
        </>
    );
}
